/**
 * Levels Toggle Component
 *
 * Action bar button that opens the Price Levels panel: tabs (Price,
 * Indicators, Volume, Gamma, Profile, Markers), search across all of them,
 * switch rows with ⚙ disclosures for secondary settings.
 *
 * Desktop: a popover capped at 70vh (and at the room left under or over the
 * button). Narrow screens (< 640px): a bottom sheet, swipe down or tap outside
 * to close. Rows and their wiring live in levels-panel/useLevelRows.tsx; every
 * setting persists exactly as before (levelsStore, gexStore, volumeProfileStore,
 * profileLevelsStore, absorptionStore, volume-pane + vest-spike stores).
 */

import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { TrendingUp } from 'lucide-react';
import { useAbsorptionStore } from '../store/absorptionStore';
import LevelsPanel from './levels-panel/LevelsPanel';
import { useLevelRows } from './levels-panel/useLevelRows';
import './LevelsToggle.css';

export { LEVEL_CONFIGS } from './levels-panel/levelConfigs';
export type { LevelConfig } from './levels-panel/levelConfigs';

export const SHEET_BREAKPOINT = 640;
const PANEL_WIDTH = 380;
const EDGE = 8;
const GAP = 4;
/** Below this much room under the button, open upwards if there is more room above. */
const MIN_ROOM_BELOW = 320;

const isNarrow = () => typeof window !== 'undefined' && window.innerWidth < SHEET_BREAKPOINT;

function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(isNarrow);
  useEffect(() => {
    const onResize = () => setNarrow(isNarrow());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
}

interface PopoverPos {
  left: number;
  width: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
}

export function computePopoverPosition(anchor: DOMRect, vw: number, vh: number): PopoverPos {
  const width = Math.min(PANEL_WIDTH, vw - EDGE * 2);
  const left = Math.min(Math.max(EDGE, anchor.left - 120), vw - width - EDGE);
  const cap = Math.floor(vh * 0.7);
  const below = vh - anchor.bottom - GAP - EDGE;
  const above = anchor.top - GAP - EDGE;
  if (below >= Math.min(cap, MIN_ROOM_BELOW) || below >= above) {
    return { left, width, top: anchor.bottom + GAP, maxHeight: Math.max(160, Math.min(cap, below)) };
  }
  return { left, width, bottom: vh - anchor.top + GAP, maxHeight: Math.max(160, Math.min(cap, above)) };
}

const LevelsToggle: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [pos, setPos] = useState<PopoverPos | null>(null);
  const [dragY, setDragY] = useState(0);
  const dragStart = useRef<number | null>(null);
  const narrow = useIsNarrow();
  const model = useLevelRows();

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);

  const handleToggleExpand = useCallback(() => setIsExpanded((prev) => !prev), []);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
    setDragY(0);
  }, []);

  // Esc / the close button hand focus back to the action-bar button; an
  // outside click leaves it where the user clicked.
  const handleCloseAndRefocus = useCallback(() => {
    handleClose();
    triggerRef.current?.focus();
  }, [handleClose]);

  // The alert switch mirrors the engine's setting; read it when the panel opens.
  useEffect(() => {
    if (isExpanded) useAbsorptionStore.getState().fetchSettings();
  }, [isExpanded]);

  // Anchor the desktop popover to the button; re-measure on resize.
  useLayoutEffect(() => {
    if (!isExpanded || narrow) return;
    const measure = () => {
      if (!containerRef.current) return;
      setPos(computePopoverPosition(containerRef.current.getBoundingClientRect(), window.innerWidth, window.innerHeight));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isExpanded, narrow]);

  // Close on click outside (the sheet's backdrop counts as outside).
  useEffect(() => {
    if (!isExpanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        expandedRef.current && !expandedRef.current.contains(e.target as Node)
      ) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded, handleClose]);

  // Esc closes even when focus is not inside the panel (the panel handles
  // Esc itself first - clearing a search - and stops it there).
  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseAndRefocus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isExpanded, handleCloseAndRefocus]);

  // The sheet takes focus when it opens (desktop focuses the search box).
  useEffect(() => {
    if (isExpanded && narrow) expandedRef.current?.focus();
  }, [isExpanded, narrow]);

  // Swipe the sheet down by its handle / header to close it.
  const dragHandlers: React.HTMLAttributes<HTMLDivElement> = {
    onTouchStart: (e) => { dragStart.current = e.touches[0].clientY; },
    onTouchMove: (e) => {
      if (dragStart.current === null) return;
      setDragY(Math.max(0, e.touches[0].clientY - dragStart.current));
    },
    onTouchEnd: () => {
      const d = dragY;
      dragStart.current = null;
      if (d > 80) handleClose();
      else setDragY(0);
    },
    onTouchCancel: () => {
      dragStart.current = null;
      setDragY(0);
    },
  };

  const { levelsEnabled: enabledCount, levelsTotal: totalCount } = model;

  const panel = (
    <LevelsPanel model={model} mobile={narrow} onClose={handleCloseAndRefocus} dragHandlers={dragHandlers} />
  );

  return (
    <div ref={containerRef} className="levels-toggle-container">
      <button
        ref={triggerRef}
        className={`action-bar-btn ${enabledCount > 0 ? 'levels-enabled' : ''}`}
        onClick={handleToggleExpand}
        title="Price Levels"
        aria-haspopup="dialog"
        aria-expanded={isExpanded}
      >
        <TrendingUp size={14} className="action-bar-icon" />
        <span className="action-bar-label">Levels</span>
        {enabledCount > 0 && enabledCount < totalCount && (
          <span className="levels-count-badge">{enabledCount}</span>
        )}
      </button>

      {isExpanded && narrow && createPortal(
        <>
          <div className="levels-sheet-backdrop" onClick={handleClose} data-testid="levels-sheet-backdrop" />
          <div
            ref={expandedRef}
            className="levels-dropdown levels-sheet"
            role="dialog"
            aria-label="Price Levels"
            data-testid="levels-sheet"
            tabIndex={-1}
            style={dragY ? { transform: `translateY(${dragY}px)`, transition: 'none' } : undefined}
          >
            {panel}
          </div>
        </>,
        document.body,
      )}

      {isExpanded && !narrow && createPortal(
        <div
          ref={expandedRef}
          className="levels-dropdown levels-popover"
          role="dialog"
          aria-label="Price Levels"
          data-testid="levels-popover"
          style={{
            position: 'fixed',
            left: pos?.left ?? EDGE,
            width: pos?.width ?? PANEL_WIDTH,
            maxHeight: pos ? `${pos.maxHeight}px` : '70vh',
            ...(pos?.bottom !== undefined ? { bottom: pos.bottom } : { top: pos?.top ?? 0 }),
            zIndex: 10000,
          }}
        >
          {panel}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default LevelsToggle;
